<script setup>
import {computed, onMounted, ref, watch} from 'vue'

const props = defineProps({
    leadId: {
        type: [Number, String],
        required: false,
        default: null,
    },
    signatures: {
        type: Array,
        required: false,
        default: () => [],
    },
})

const signatures = ref([])
const loading = ref(false)
const loaded = ref(false)

const applySignatures = (items = []) => {
    signatures.value = items
}

const loadSignatures = async () => {
    if (!props.leadId) {
        applySignatures(props.signatures)
        loaded.value = true
        return
    }

    loading.value = true

    try {
        const response = await axios.get(route('bo.api.v1.crm.lead.signature-requests', props.leadId))

        applySignatures(response.data.signature_requests ?? [])
    } finally {
        loading.value = false
        loaded.value = true
    }
}

const normalizedSignatures = computed(() => {
    return signatures.value.map((signature) => {
        const signingEnabled = signature.expert_contract_signing_enabled ?? !signature.disabled

        return {
            ...signature,
            expert_contract_title: signature.expert_contract_title ?? signature.contract?.title ?? '',
            expert_contract_signing_enabled: signingEnabled,
        }
    })
})

watch(
    () => props.signatures,
    (nextSignatures) => {
        if (!props.leadId) {
            applySignatures(nextSignatures)
            loaded.value = true
        }
    },
    {deep: true, immediate: true}
)

watch(
    () => props.leadId,
    (nextLeadId, previousLeadId) => {
        if (nextLeadId && nextLeadId !== previousLeadId) {
            loadSignatures()
        }
    }
)

onMounted(() => {
    loadSignatures()
})
</script>

<template>
    <div
        v-if="loading && !loaded"
        class="flex justify-center"
    >
        <mf-spinner-medium class="w-12 h-6" />
    </div>

    <list-basic>
        <list-basic-item
            v-for="signature in normalizedSignatures"
            :key="signature.id"
        >
            <a
                :href="route('expert-contracts.edit', { expert_contract: signature.expert_contract_id })"
                target="_blank"
                class="float-right"
            >
                <button-secondary size="sm">
                    {{ _mft('crm:signaturesList.goToContract') }}
                </button-secondary>
            </a>

            <h5 class="text-gray-800 mb-0">
                {{ signature.expert_contract_title }}
            </h5>
            <span v-if="signature.is_signed" class="text-xs">
                <i class="mr-1 fa-solid fa-signature text-myflowGreen-600"></i>
                <span class="text-gray-500">{{ _mft('crm:signaturesList.signed') }}</span>
            </span>
            <span v-else-if="signature.expert_contract_signing_enabled" class="text-xs">
                <i class="mr-1 text-myflowRed-600 fa-solid fa-signature-slash"></i>
                <span class="text-gray-500">{{ _mft('crm:signaturesList.awaitingSignature') }}</span>
            </span>
            <span v-else class="text-xs">
                <i class="mr-1 text-myflowYellow-600 fa-solid fa-signature-slash"></i>
                <span class="text-gray-500">{{ _mft('crm:signaturesList.signingNotEnabled') }}</span>
            </span>
        </list-basic-item>

        <div v-if="normalizedSignatures.length === 0" class="text-center py-12 text-gray-500">
            <i>{{ _mft('crm:signaturesList.noContractsSent') }}</i>
        </div>
    </list-basic>
</template>
